import React, { useContext, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

import ModalTemplate from './_ModalTemplate';
import SimpleSelect from '../Components/SimpleSelect';
import SimpleTextInput from '../Components/SimpleTextInput';
import { GBENSContext } from '../GBENSContainer';
import { MANDATORY_EORI_COUNTRIES } from '../utils';

export const TraderDetailsModal = ({
  open,
  setOpen,
  defaultValues,
  setDefaultValues,
  screen,
  modal,
  consignment = true,
}) => {
  const modalLocal = '_' + modal;

  const {
    data,
    setErrors,
    moveData,
    updateData,
    referenceData,
    errorsLocal,
    setErrorsLocal,
    checkErrorsConsignmentLocal,
    checkErrorsConsignment,
    moveConsignmentData,
    deleteConsignmentData,
    setBlankConsignmentData,
    checkErrorsLocal,
    currentConsignment,
    isEditable,
    isReadOnly,
  } = useContext(GBENSContext);

  const [showAlert, setShowAlert] = useState(false);
  const [errorsChecked, setErrorsChecked] = useState(false);
  const [currentCountry, setCurrentCountry] = useState('');

  const isConsignorOrSeller = modal === 'Consignor' || modal === 'Seller';

  let isTraderEditable = true;

  if (isReadOnly) {
    isTraderEditable = false;
  } else if (consignment) {
    isTraderEditable = currentConsignment.isEditable || !currentConsignment.id;
  } else {
    isTraderEditable = isEditable;
  }

  useEffect(() => {
    const initializeData = async () => {
      moveData(screen, modal, modalLocal, false);

      if (defaultValues) {
        updateData({ ...defaultValues }, screen, modalLocal, null, false);
      }

      const errors = await checkErrorsLocal(modal, screen, true);
      setErrorsLocal(errors);
    };

    const initializeConsignmentData = async () => {
      if (!defaultValues) {
        moveConsignmentData(modal, modalLocal, false);
        const errors = await checkErrorsConsignmentLocal(modal, true);
        setErrorsLocal(errors);
      } else {
        setBlankConsignmentData(modalLocal, defaultValues);
      }
    };

    // If the modal is open, run the initialization logic
    if (open) {
      consignment ? initializeConsignmentData() : initializeData();
    }
  }, [open, defaultValues]);

  const dontSave = () => {
    setDefaultValues?.(null);

    if (consignment) {
      deleteConsignmentData(modalLocal);
    }

    setOpen(false);
    setErrorsLocal(null);
  };

  const save = async () => {
    if (consignment) {
      const errors = await checkErrorsConsignmentLocal(modal);
      setErrorsLocal(errors);
      if (!errors?.[modal]) setErrorsChecked(true);
    } else {
      const errors = await checkErrorsLocal(modal, screen);
      setErrorsLocal(errors);
      if (!errors?.[screen]?.[modal]) {
        setErrors((prev) => {
          const screenErrors = { ...prev?.[screen] };
          delete screenErrors[modal];
          return { ...prev, [screen]: screenErrors };
        });
        setErrorsChecked(true);
      }
    }
  };

  useEffect(() => {
    if (errorsLocal?.[modal]?.modalError) setShowAlert(true);
  }, [errorsLocal]);

  useEffect(() => {
    if (errorsChecked) {
      if (consignment) {
        setShowAlert(false);
        moveConsignmentData(modalLocal, modal, true);
        setOpen(false);
        setErrorsChecked(false);
        setErrorsLocal(null);
        setTimeout(() => {
          checkErrorsConsignment();
        }, 100);
        setDefaultValues(null);
      } else {
        moveData(screen, modalLocal, modal, true);
        setOpen(false);
        setErrorsChecked(false);
        setErrorsLocal(null);
        setDefaultValues(null);
      }
    }
  }, [errorsChecked]);

  useEffect(() => {
    const newCountry = consignment ? currentConsignment?.[modalLocal]?.Country : data?.[screen]?.[modalLocal]?.Country;
    setCurrentCountry(newCountry);
  }, [data, currentConsignment]);

  const rowProps = {
    leftColumnWidth: '40%',
    rightColumnWidth: '60%',
  };

  // EORI is required if not a Consignor AND selected country is not Norway or Switzerland
  const isEORIRequired = !isConsignorOrSeller && MANDATORY_EORI_COUNTRIES.includes(currentCountry);

  return (
    <ModalTemplate
      open={open}
      onClose={() => setOpen(false)}
      title={`${modal || 'Trader'} Details`}
      value={`${modal} Details`}
      body={
        <div>
          {showAlert && (
            <Alert variant="filled" severity="error">
              There are errors with the {modal}
            </Alert>
          )}
          <SimpleSelect
            name="Country*"
            modal={modalLocal}
            id="Country"
            options={referenceData.countryOfOriginCC}
            localErrors
            rowProps={rowProps}
            consignment={consignment}
            screen={screen}
            disabled={!isTraderEditable}
            isAutocomplete
          />
          <SimpleTextInput
            name={isEORIRequired ? 'EORI*' : 'EORI'}
            modal={modalLocal}
            consignment={consignment}
            id="eori"
            localErrors
            rowProps={rowProps}
            screen={screen}
            disabled={!isTraderEditable}
            placeholder={isEORIRequired ? '' : 'This field is not required'}
          />
          <SimpleTextInput
            name="Company Name*"
            modal={modalLocal}
            consignment={consignment}
            id="companyName"
            localErrors
            rowProps={rowProps}
            screen={screen}
            disabled={!isTraderEditable}
          />
          <SimpleTextInput
            name="Street Number*"
            modal={modalLocal}
            consignment={consignment}
            id="streetNumber"
            localErrors
            rowProps={rowProps}
            screen={screen}
            disabled={!isTraderEditable}
          />
          <SimpleTextInput
            name="Street Address*"
            modal={modalLocal}
            consignment={consignment}
            id="streetAddress"
            localErrors
            rowProps={rowProps}
            screen={screen}
            disabled={!isTraderEditable}
          />
          <SimpleTextInput
            name="Town / City*"
            modal={modalLocal}
            consignment={consignment}
            id="townCity"
            localErrors
            rowProps={rowProps}
            screen={screen}
            disabled={!isTraderEditable}
          />
          <SimpleTextInput
            name="Postcode*"
            modal={modalLocal}
            consignment={consignment}
            id="postCode"
            localErrors
            rowProps={rowProps}
            screen={screen}
            disabled={!isTraderEditable}
          />
        </div>
      }
      buttons={[
        <Button key={0} onClick={dontSave}>
          Close
        </Button>,
        <Button key={1} onClick={save}>
          Add
        </Button>,
      ]}
    />
  );
};

TraderDetailsModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  defaultValues: PropTypes.bool,
  setDefaultValues: PropTypes.func,
  modal: PropTypes.oneOf(['Consignor', 'Consignee', 'Seller']).isRequired,
};

export const ConsignmentDetailsModal = ({ open, setOpen, disabled }) => {
  const { saveConsignmentLine, deleteConsignmentLine, referenceData, currentLine, updateCurrentLine } =
    useContext(GBENSContext);

  const [disablePiecesAndMarks, setDisablePiecesAndMarks] = useState(false);

  useEffect(() => {
    setDisablePiecesAndMarks(false);
    if (currentLine?.packageType) {
      const packageType = referenceData.packageTypes.find(
        (pt) => parseInt(pt.key) === parseInt(currentLine.packageType)
      );
      if (packageType?.disablePiecesRequired) {
        setDisablePiecesAndMarks(true);
      }
    }
  }, [currentLine, referenceData, open]);

  useEffect(() => {
    if (disablePiecesAndMarks) {
      // clear the values of the disabled fields - as they should be blank on the backend
      updateCurrentLine({ target: { value: '' } }, 'packagingMarks');
      updateCurrentLine({ target: { value: '' } }, 'numPieces');
    }
  }, [disablePiecesAndMarks]);

  const save = () => {
    if (!disabled) {
      saveConsignmentLine();
    }
    setOpen(false);
  };
  const deleteLine = () => {
    if (!disabled) {
      deleteConsignmentLine();
    }
    setOpen(false);
  };

  const rowProps = {
    leftColumnWidth: '40%',
    rightColumnWidth: '60%',
  };

  return (
    <ModalTemplate
      open={open}
      onClose={() => setOpen(false)}
      title="Consignment Details"
      value="Goods Item Number: Line x"
      body={
        <div className="consignment-line-form">
          <SimpleTextInput
            name="Commodity Code*"
            item
            id="commodityCode"
            rowProps={rowProps}
            placeholder="Enter the HS Code for the goods"
            toolTip="Enter at least the first 6 characters of a valid commodity code"
            disabled={disabled}
          />
          <SimpleTextInput
            name="Goods Description*"
            item
            id="goodsDescription"
            rowProps={rowProps}
            placeholder="Enter an accurate goods description"
            disabled={disabled}
          />
          <SimpleSelect
            name="Type Of Packaging*"
            item
            id="packageType"
            options={referenceData.packageTypes}
            rowProps={rowProps}
            placeholder="Select the type of packaging"
            disabled={disabled}
            isAutocomplete
          />
          <SimpleTextInput
            name={`Packaging Marks${disablePiecesAndMarks ? '' : '*'}`}
            item
            id="packagingMarks"
            rowProps={rowProps}
            disabled={disabled || disablePiecesAndMarks}
            toolTip={
              disablePiecesAndMarks
                ? 'Packaging Marks can not be declared for the Type Of Packaging selected'
                : undefined
            }
          />
          <SimpleTextInput
            name={`Number of Pieces / Packages${disablePiecesAndMarks ? '' : '*'}`}
            item
            id="numPieces"
            rowProps={rowProps}
            disabled={disabled || disablePiecesAndMarks}
            toolTip={
              disablePiecesAndMarks
                ? 'Number of Pieces / Packages can not be declared for the Type Of Packaging selected'
                : undefined
            }
          />
          <SimpleTextInput name="Gross Weight*" weight item id="grossWeight" rowProps={rowProps} disabled={disabled} />
          <SimpleSelect
            name="UN Dangerous Goods Code"
            item
            id="dangerousGoods"
            options={referenceData.unDangerousGoods}
            rowProps={rowProps}
            placeholder="Select a dangerous goods code (if applicable)"
            disabled={disabled}
            isAutocomplete
            allowUserInputAsValue
          />
        </div>
      }
      buttons={[
        <Button key={0} onClick={deleteLine} disabled={disabled}>
          Delete This Line
        </Button>,
        <Button key={1} onClick={save}>
          {disabled ? 'Close' : 'Save and Close'}
        </Button>,
      ]}
    />
  );
};

ConsignmentDetailsModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
};

export const InvalidMRNModal = ({ open, setOpen, modalMessage }) => {
  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 650,
          bgcolor: 'background.paper',
          border: 'none',
          outline: 'none',
          p: 4,
          borderRadius: 5,
        }}
      >
        <Typography id="modal-modal-title" variant="h6" component="h2">
          {modalMessage.title}
        </Typography>
        <Typography variant="p" component="p">
          {modalMessage.body}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 3,
          }}
        >
          <Button key={0} onClick={() => setOpen(false)}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

InvalidMRNModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
};
